import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  GlReplacementFormModal,
  GlReplacementViewModal,
} from 'components/modals/modules/gl-replacement-module/GlReplacementModal';
import type {
  GlReplacementFormData,
  GlReplacementRecord,
  OutgoingGLFormData,
  IncomingGLFormData,
} from 'components/modals/modules/gl-replacement-module/GlReplacementModal';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useGlReplacements, useCreateGlReplacement, useUpdateGlReplacement, useSubmitGlReplacement, useDeleteGlReplacement } from 'hooks/api';
import { glReplacementService } from 'api/services/glReplacementService';
import { loanMappingService } from 'api/services/loanMappingService';
import { GridLoadingSkeleton } from 'components/common';

// ============================================================================
// EMPTY FORM DATA
// ============================================================================

const emptyFormData: GlReplacementFormData = {
  mappingId: '',
  mappingRef: '',
  customerName: '',
  customerId: '',
  loanAccountNumber: '',
  addonOutstandingPrincipal: '',
  addonOutstandingInterest: '',
  availableGls: [],
  incomingEntries: [],
};

// ============================================================================
// MAIN GL REPLACEMENT SCREEN
// ============================================================================

const GlReplacementScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useGlReplacements();
  const { createGlReplacement } = useCreateGlReplacement();
  const { submitGlReplacement } = useSubmitGlReplacement();
  const { updateGlReplacement } = useUpdateGlReplacement();
  const { deleteGlReplacement } = useDeleteGlReplacement();

  const replacements = (apiData as unknown as GlReplacementRecord[]) || [];

  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GlReplacementRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<GlReplacementRecord | null>(null);
  const [formData, setFormData] = useState<GlReplacementFormData>(emptyFormData);
  const [statusFilter, setStatusFilter] = useState('');

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    loading: boolean;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', loading: false, onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() =>
    replacements.filter((r) => {
      const matchesSearch = !searchQuery ||
        (r.replacementRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.mappingRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.customerId ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === '' || r.statusCode === statusFilter;
      return matchesSearch && matchesStatus;
    }),
    [replacements, searchQuery, statusFilter]
  );

  // ============================================================================
  // HELPERS: Build payload from form data
  // ============================================================================

  const buildPayload = (data: GlReplacementFormData) => ({
    addonOutstandingPrincipal: parseFloat(data.addonOutstandingPrincipal) || 0,
    addonOutstandingInterest: parseFloat(data.addonOutstandingInterest) || 0,
    outgoingEntries: data.availableGls
      .filter((gl) => gl.selected)
      .map((gl) => ({
        outgoingMappedGlId: gl.mappedGlId,
        outgoingGlOutstandingPrincipal: parseFloat(gl.outstandingPrincipal) || 0,
        outgoingGlOutstandingInterest: parseFloat(gl.outstandingInterest) || 0,
        tokenLossCharge: parseFloat(gl.tokenLossCharge) || 0,
      })),
    incomingEntries: data.incomingEntries.map((e) => ({
      incomingGlNumber: e.incomingGlNumber,
      incomingCustomerId: e.incomingCustomerId || undefined,
      incomingNetWeight: parseFloat(e.incomingNetWeight) || 0,
      incomingOriginalLoanAmount: parseFloat(e.incomingOriginalLoanAmount) || 0,
      incomingLoanDate: e.incomingLoanDate || undefined,
      documentationCharge: parseFloat(e.documentationCharge) || 0,
    })),
  });

  const validateForm = (): boolean => {
    if (!formData.mappingId) { showMessage('Please search for a loan mapping first', 'error'); return false; }
    if (!formData.addonOutstandingPrincipal || parseFloat(formData.addonOutstandingPrincipal) <= 0) { showMessage('Outstanding Principal is required', 'error'); return false; }
    const selectedOutgoing = formData.availableGls.filter((gl) => gl.selected);
    if (selectedOutgoing.length === 0) { showMessage('Select at least one outgoing GL', 'error'); return false; }
    if (formData.incomingEntries.length === 0) { showMessage('Add at least one incoming GL entry', 'error'); return false; }
    const incompleteIncoming = formData.incomingEntries.some((e) => !e.incomingGlNumber || !e.incomingNetWeight);
    if (incompleteIncoming) { showMessage('Complete all incoming GL entries (GL Number and Eligible Weight are required)', 'error'); return false; }
    return true;
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData({ ...emptyFormData });
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(async (record: GlReplacementRecord) => {
    try {
      const [replResponse, mappingResponse] = await Promise.all([
        glReplacementService.getById(record.id),
        loanMappingService.getById(record.mappingId),
      ]);
      const detail = replResponse.data;
      const mappingDetail = mappingResponse.data;

      // Build outgoing GL IDs set from saved entries
      const savedOutgoingIds = new Set(
        (detail?.outgoingEntries ?? []).map((e: any) => String(e.outgoingMappedGlId))
      );
      // Build a lookup for saved outgoing P/I values
      const outgoingLookup = new Map<string, { principal: string; interest: string; tokenLossCharge: string }>();
      (detail?.outgoingEntries ?? []).forEach((e: any) => {
        outgoingLookup.set(String(e.outgoingMappedGlId), {
          principal: String(e.outgoingGlOutstandingPrincipal ?? ''),
          interest: String(e.outgoingGlOutstandingInterest ?? ''),
          tokenLossCharge: String(e.tokenLossCharge ?? ''),
        });
      });

      const openGls: OutgoingGLFormData[] = (mappingDetail?.mappedGls ?? [])
        .filter((gl) => gl.statusCode === 'OPEN')
        .map((gl) => {
          const glId = String(gl.id);
          const saved = outgoingLookup.get(glId);
          return {
            mappedGlId: glId,
            glNumber: gl.glNumber ?? '',
            customerName: gl.glCustomerName ?? '',
            netWeight: gl.netWeight,
            allocationRatio: gl.allocationRatio,
            selected: savedOutgoingIds.has(glId),
            outstandingPrincipal: saved?.principal ?? '',
            outstandingInterest: saved?.interest ?? '',
            tokenLossCharge: saved?.tokenLossCharge ?? '',
          };
        });

      const editIncomingEntries: IncomingGLFormData[] = (detail?.incomingEntries ?? []).map((e: any) => ({
        incomingGlNumber: e.incomingGlNumber ?? '',
        incomingCustomerId: e.incomingCustomerId ?? '',
        incomingNetWeight: String(e.incomingNetWeight ?? ''),
        incomingOriginalLoanAmount: String(e.incomingOriginalLoanAmount ?? ''),
        incomingLoanDate: e.incomingLoanDate ? e.incomingLoanDate.substring(0, 10) : '',
        documentationCharge: String(e.documentationCharge ?? ''),
      }));

      setFormMode('edit');
      setSelectedRecord(record);
      setFormData({
        mappingId: record.mappingId,
        mappingRef: record.mappingRef ?? '',
        customerName: record.customerName,
        customerId: record.customerId,
        loanAccountNumber: record.loanAccountNumber ?? '',
        addonOutstandingPrincipal: String(detail?.addonOutstandingPrincipal ?? record.addonOutstandingPrincipal),
        addonOutstandingInterest: String(detail?.addonOutstandingInterest ?? record.addonOutstandingInterest),
        availableGls: openGls,
        incomingEntries: editIncomingEntries,
      });
      setFormModalOpen(true);
    } catch {
      showMessage('Failed to load GL replacement details for editing', 'error');
    }
  }, [showMessage]);

  const handleView = useCallback((record: GlReplacementRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    if (!validateForm()) return;

    const action = formMode === 'create' ? 'Create' : 'Save';
    setConfirmDialog({
      open: true,
      title: `${action} GL Replacement`,
      message: formMode === 'create'
        ? 'Are you sure you want to create this GL replacement as Draft?'
        : 'Are you sure you want to save changes to this GL replacement?',
      variant: 'default',
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          if (formMode === 'create') {
            await createGlReplacement({ mappingId: formData.mappingId, ...buildPayload(formData) } as any);
            await refetch();
            showMessage('GL replacement created as Draft', 'success');
          } else if (selectedRecord) {
            await updateGlReplacement(selectedRecord.id, {
              ...buildPayload(formData),
              rowVersion: selectedRecord.rowVersion ?? 0,
            });
            await refetch();
            showMessage('GL replacement updated successfully', 'success');
          }
          setFormModalOpen(false);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
        } catch (err: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          const msg = err?.response?.data?.message || 'Failed to save GL replacement';
          showMessage(msg, 'error');
        }
      },
    });
  }, [formMode, formData, selectedRecord, showMessage, createGlReplacement, updateGlReplacement, refetch]);

  const handleDelete = useCallback((record: GlReplacementRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete GL Replacement',
      message: `Are you sure you want to delete GL replacement ${record.replacementRef}?`,
      variant: 'danger',
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteGlReplacement(record.id);
          await refetch();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          showMessage('GL replacement deleted successfully', 'success');
        } catch (err: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          const msg = err?.response?.data?.message || 'Failed to delete GL replacement';
          showMessage(msg, 'error');
        }
      },
    });
  }, [showMessage, deleteGlReplacement, refetch]);

  const handleSubmit = useCallback((record: GlReplacementRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Submit for Approval',
      message: `Are you sure you want to submit GL replacement ${record.replacementRef} (Net Cash Flow: ${formatCurrency(record.netCashFlow)}) for approval?`,
      variant: 'default',
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await submitGlReplacement(record.id);
          await refetch();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          showMessage('GL replacement submitted for approval', 'success');
        } catch (err: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          const msg = err?.response?.data?.message || 'Failed to submit GL replacement';
          showMessage(msg, 'error');
        }
      },
    });
  }, [showMessage, submitGlReplacement, refetch]);

  const handleDirectSubmit = useCallback(() => {
    if (!validateForm()) return;

    setConfirmDialog({
      open: true,
      title: 'Submit GL Replacement',
      message: 'Are you sure you want to submit this GL replacement directly for approval?',
      variant: 'default',
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          let recordId: string;
          if (formMode === 'edit' && selectedRecord) {
            await updateGlReplacement(selectedRecord.id, {
              ...buildPayload(formData),
              rowVersion: selectedRecord.rowVersion ?? 0,
            });
            recordId = selectedRecord.id;
          } else {
            const created = await createGlReplacement({ mappingId: formData.mappingId, ...buildPayload(formData) } as any);
            recordId = String(created?.id);
          }
          if (recordId) await submitGlReplacement(recordId);
          await refetch();
          showMessage('GL replacement submitted for approval', 'success');
          setFormModalOpen(false);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
        } catch (err: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          const msg = err?.response?.data?.message || 'Failed to submit GL replacement';
          showMessage(msg, 'error');
        }
      },
    });
  }, [formMode, formData, selectedRecord, showMessage, createGlReplacement, updateGlReplacement, submitGlReplacement, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: GlReplacementRecord): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      {
        id: 'view',
        label: 'View Details',
        icon: <IconSystem.eye size={18} />,
        onClick: () => handleView(row),
      },
    ];

    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({
        id: 'edit',
        label: 'Edit',
        icon: <IconSystem.edit size={18} />,
        onClick: () => handleEdit(row),
      });
    }

    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({
        id: 'submit',
        label: row.statusCode === 'RETURNED' ? 'Resubmit for Approval' : 'Submit for Approval',
        icon: <IconSystem.check size={18} />,
        onClick: () => handleSubmit(row),
      });
    }

    if (row.statusCode === 'DRAFT') {
      actions.push({
        id: 'delete',
        label: 'Delete',
        icon: <IconSystem.trash size={18} />,
        onClick: () => handleDelete(row),
        variant: 'danger' as const,
      });
    }

    return actions;
  }, [handleView, handleEdit, handleSubmit, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<GlReplacementRecord>[] = useMemo(() => [
    { id: 'replacementRef', header: 'Replacement Ref', accessorKey: 'replacementRef', size: 150 },
    { id: 'mappingRef', header: 'Mapping Ref', accessorKey: 'mappingRef', size: 140 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 140 },
    { id: 'businessUnitName', header: 'Branch', accessorKey: 'businessUnitName', size: 130, cell: ({ getValue }: any) => getValue() || '-' },
    {
      id: 'outgoingWeight',
      header: 'Out Wt (g)',
      accessorKey: 'totalOutgoingWeight',
      size: 100,
      cell: ({ getValue }: any) => <span>{(getValue() ?? 0).toFixed(3)}</span>,
    },
    {
      id: 'incomingWeight',
      header: 'In Wt (g)',
      accessorKey: 'totalIncomingWeight',
      size: 100,
      cell: ({ getValue }: any) => <span>{(getValue() ?? 0).toFixed(3)}</span>,
    },
    {
      id: 'weightDiff',
      header: 'Wt Diff (g)',
      accessorKey: 'weightDifference',
      size: 100,
      cell: ({ getValue }: any) => {
        const val = getValue() ?? 0;
        return <span style={{ fontWeight: 500, color: val > 0 ? '#dc2626' : '#16a34a' }}>{val.toFixed(3)}</span>;
      },
    },
    {
      id: 'settlementRatio',
      header: 'Sett. Ratio',
      accessorKey: 'settlementRatio',
      size: 100,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 500 }}>{((getValue() ?? 0) * 100).toFixed(2)}%</span>,
    },
    {
      id: 'netCashFlow',
      header: 'Net Cash Flow',
      accessorKey: 'netCashFlow',
      size: 140,
      cell: ({ getValue }: any) => {
        const val = getValue() ?? 0;
        return <span style={{ fontWeight: 600, color: val >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(val)}</span>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 150,
      cell: ({ getValue }: any) => {
        const status = getValue();
        return (
          <span style={getStatusBadgeStyle(status ?? '')}>
            {(status ?? '').replace(/_/g, ' ')}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'New GL Replacement',
      onClick: handleAddNew,
      tooltip: 'Create New GL Replacement',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => showMessage('Export functionality will be available after API integration', 'info'),
      tooltip: 'Export',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="GL Replacements"
        icon={<IconSystem.refresh size={20} />}
        description="Replace Gold Loans within active loan mappings."
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search replacements...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && replacements.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load GL replacements</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && replacements.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
          <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                enableRowSelection
                enablePagination
                enableColumnFilters={false}
                rowActions={getRowActions}
                frozenRightColumns={['actions']}
                onSelectionChange={setSelectedRows}
                emptyMessage="No GL replacements found"
              />
            </div>
          </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'RETURNED', label: 'Returned' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); }}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <GlReplacementFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onSubmit={handleDirectSubmit}
        onClose={() => setFormModalOpen(false)}
        isProcessing={confirmDialog.loading}
      />

      {/* View Modal */}
      <GlReplacementViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default GlReplacementScreen;
